import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container } from "reactstrap";
import "react-toastify/dist/ReactToastify.css";
import { Search } from "lucide-react";
import { toast } from "react-toastify";

import {
  FetchListingsRequests,
  PermanentlyDeleteProperty,
} from "../../apis/Property";
import { GenerateShortToken } from "../../apis/Auth";
import Paginate from "../../components/shared/paginate";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/shared/alertDialog";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

const cellClass =
  "min-w-[130px] flex-1 px-1 content-center border-gray-500 break-words py-2 text-center dark:border-white";

const ListingDeleteRequests = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.MANAGE_LISTINGS);

  const [properties, setProperties] = useState([]);
  const [currentPage, setCurrentPage] = useState("1");
  const [totalPages, setTotalPages] = useState("1");
  const [keyword, setKeyword] = useState("");
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const limit = "10";

  const [shortToken, setShortToken] = useState("");
  const tokenTimestampRef = useRef(0);
  const generatingPromiseRef = useRef(null);
  const TOKEN_TTL_MS = 3 * 60 * 1000;

  const columns = useMemo(
    () => [
      "ID",
      "Title",
      "Host",
      "Email",
      "Mobile",
      "City",
      "Listed",
      "Ready",
      "Reason",
      "Requested",
      "View",
      "Action",
    ],
    [],
  );

  useEffect(() => {
    const generateShortToken = async () => {
      try {
        const response = await GenerateShortToken();
        const token = response.data?.token || "";
        setShortToken(token);
        tokenTimestampRef.current = Date.now();
      } catch (err) {
        toast.error("Failed to generate short token");
      }
    };
    generateShortToken();
  }, []);

  const getValidShortToken = async () => {
    if (
      shortToken &&
      tokenTimestampRef.current &&
      Date.now() - tokenTimestampRef.current < TOKEN_TTL_MS
    ) {
      return shortToken;
    }

    if (generatingPromiseRef.current) {
      try {
        await generatingPromiseRef.current;
        return shortToken;
      } catch {
        return "";
      }
    }

    generatingPromiseRef.current = (async () => {
      try {
        const response = await GenerateShortToken();
        const token = response.data?.token || "";
        setShortToken(token);
        tokenTimestampRef.current = Date.now();
        return token;
      } catch (err) {
        toast.error("Failed to generate short token");
        return "";
      } finally {
        generatingPromiseRef.current = null;
      }
    })();

    return (await generatingPromiseRef.current) || "";
  };

  const fetchDeleteRequests = async () => {
    try {
      const response = await FetchListingsRequests(
        currentPage,
        limit,
        "delete",
      );
      const listings = response?.data?.listings || [];
      const filtered = keyword
        ? listings.filter((property) => {
            const haystack = [
              property._id,
              property.title,
              property.host?.firstName,
              property.host?.lastName,
              property.host?.email,
              property.host?.mobileNumber,
              property.location?.city?.en,
              property.deleteRequest?.reason,
            ]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();
            return haystack.includes(keyword.trim().toLowerCase());
          })
        : listings;
      setProperties(filtered);
      setTotalPages(String(response?.data?.totalPages || 1));
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          "Failed to load listing delete requests",
      );
    }
  };

  useEffect(() => {
    fetchDeleteRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  useEffect(() => {
    setCurrentPage("1");
    const timer = setTimeout(() => {
      fetchDeleteRequests();
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyword]);

  const confirmPermanentDelete = (property) => {
    setSelectedProperty(property);
    setConfirmOpen(true);
  };

  const handlePermanentDelete = async () => {
    if (!selectedProperty?._id || submitting) return;
    setSubmitting(true);
    try {
      const response = await PermanentlyDeleteProperty(selectedProperty._id);
      if (response?.success) {
        toast.success("Listing permanently deleted");
        setProperties((prev) =>
          prev.filter((property) => property._id !== selectedProperty._id),
        );
        setConfirmOpen(false);
        setSelectedProperty(null);
      } else {
        toast.error(response?.message || "Failed to delete listing");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to delete listing",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (value) => {
    if (!value) return "—";
    return String(value).slice(0, 10);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container className="px-0 dark:bg-darkBg1 dark:text-[#c7d8eb]" fluid>
          <div className="flex flex-col gap-6 p-6">
            <div>
              <h1 className="text-2xl dark:text-[#c7d8eb]">
                Listing delete requests
              </h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                Hosts requested permanent removal of these listings. Includes
                in-progress, unlisted, and listed properties.
              </p>
            </div>

            <div className="lg:flex flex-col lg:border lg:border-b-0 border-gray-400 dark:border-white">
              <div className="border-x border-t lg:border-none border-gray-500 dark:border-white rounded-[6px_6px_0px_0px] w-full flex flex-col lg:flex-row justify-between gap-4 p-4">
                <div className="relative border max-w-[400px] border-gray-500 dark:border-white rounded-md w-full">
                  <Search
                    size={16}
                    className="absolute left-2 bottom-1/2 translate-y-1/2"
                  />
                  <input
                    className="pl-8 pr-6 py-2 text-base rounded-md w-full dark:bg-darkBg2"
                    type="search"
                    placeholder="Search"
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <div className="hidden lg:flex">
                  {columns.map((column, index) => (
                    <div
                      key={`column-${index}`}
                      className={`${cellClass} border-y font-semibold`}
                    >
                      {column}
                    </div>
                  ))}
                </div>

                {properties.length === 0 && (
                  <div className="border-b border-gray-500 p-6 text-center dark:border-white">
                    No listing delete requests.
                  </div>
                )}

                {properties.map((property) => (
                  <React.Fragment key={property._id}>
                    <div className="hidden lg:flex">
                      <div className={`${cellClass} border-b`}>
                        {property._id}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {property.title || "—"}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {[property.host?.firstName, property.host?.lastName]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {property.host?.email || "—"}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {property.host?.mobileNumber || "—"}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {property.location?.city?.en || "—"}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {property.listed ? "Yes" : "No"}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {property.ready ? "Yes" : "No"}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {property.deleteRequest?.reason || "—"}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {formatDate(property.deleteRequest?.requestedAt)}
                      </div>
                      <div className={`${cellClass} border-b`}>
                        <button
                          type="button"
                          className="text-secondaryColor"
                          onClick={async () => {
                            const token = await getValidShortToken();
                            if (token) {
                              window.open(
                                `${process.env.REACT_APP_CLIENTSIDE_URL}/rooms/${property._id}/preview?token=${token}`,
                                "_blank",
                              );
                            }
                          }}
                        >
                          View
                        </button>
                      </div>
                      <div className={`${cellClass} border-b`}>
                        {canManage ? (
                          <button
                            type="button"
                            className="font-semibold text-red-600"
                            onClick={() => confirmPermanentDelete(property)}
                          >
                            Delete permanently
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    </div>

                    <div className="mb-6 block border border-gray-500 dark:border-white lg:hidden">
                      {[
                        ["ID", property._id],
                        ["Title", property.title || "—"],
                        [
                          "Host",
                          [property.host?.firstName, property.host?.lastName]
                            .filter(Boolean)
                            .join(" ") || "—",
                        ],
                        ["Email", property.host?.email || "—"],
                        ["Mobile", property.host?.mobileNumber || "—"],
                        ["City", property.location?.city?.en || "—"],
                        ["Listed", property.listed ? "Yes" : "No"],
                        ["Ready", property.ready ? "Yes" : "No"],
                        ["Reason", property.deleteRequest?.reason || "—"],
                        [
                          "Requested",
                          formatDate(property.deleteRequest?.requestedAt),
                        ],
                      ].map(([label, value]) => (
                        <div
                          key={`${property._id}-${label}`}
                          className="flex justify-between gap-2 border-b border-gray-500 p-2 dark:border-white"
                        >
                          <div className="font-bold">{label}</div>
                          <div className="break-all text-right">{value}</div>
                        </div>
                      ))}
                      <div className="flex justify-between gap-2 border-b border-gray-500 p-2 dark:border-white">
                        <div className="font-bold">View</div>
                        <button
                          type="button"
                          className="text-secondaryColor"
                          onClick={async () => {
                            const token = await getValidShortToken();
                            if (token) {
                              window.open(
                                `${process.env.REACT_APP_CLIENTSIDE_URL}/rooms/${property._id}/preview?token=${token}`,
                                "_blank",
                              );
                            }
                          }}
                        >
                          View
                        </button>
                      </div>
                      <div className="flex justify-between gap-2 p-2">
                        <div className="font-bold">Action</div>
                        {canManage ? (
                          <button
                            type="button"
                            className="font-semibold text-red-600"
                            onClick={() => confirmPermanentDelete(property)}
                          >
                            Delete permanently
                          </button>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>

            <Paginate
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              currentPage={currentPage}
            />
          </div>
        </Container>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="select-none bg-gray-200 dark:bg-[#171d37] dark:text-[#c7d8eb]">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete listing?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove{" "}
              <strong>{selectedProperty?.title || "this listing"}</strong> and
              its photos. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={submitting}
              onClick={handlePermanentDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {submitting ? "Deleting..." : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </React.Fragment>
  );
};

export default ListingDeleteRequests;
